import React, { useState, useEffect } from 'react';
import Input from '../../components/Input';
import CheckBox from '../../components/CheckBox';
import { permissionsList } from '../../config/permissions';
import Button from '../../components/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../../services/api';
import { QUERY_KEYS } from '../../config/queryKey';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../components/auth/AuthProvider';

export default function RoleForm({ currentRole, actions, onCallback }) {
  const queryClient = useQueryClient();
  const { token } = useAuthContext();

  const [error, setError] = useState(null);
  const [role, setRole] = useState({
    roleKey: '',
    roleName: '',
    permissions: [],
  });

  const handleChange = (e) => {
    setRole((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePermissionChange = (e, permissionId) => {
    const { checked } = e.target;
    if (checked) {
      setRole((prev) => ({
        ...prev,
        permissions: [...prev.permissions, permissionId],
      }));
    } else {
      setRole((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => p !== permissionId),
      }));
    }
  };

  const mutation = useMutation({
    mutationFn: (data) => {
      setError(null);
      if (currentRole?.id) {
        return API.role.updateRole(data);
      } else {
        return API.role.createRole(data);
      }
    },
    onSuccess: (data) => {
      toast.success('Role saved successfully');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_ROLES] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.AUTH.ME, token],
      });
      onCallback(data);
    },
    onError: (error) => {
      setError(error);
    },
  });

  useEffect(() => {
    if (currentRole) {
      setRole(currentRole);
    }
  }, [currentRole]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      )}
      <div className="space-y-4">
        <Input
          id="role-key"
          label="Role Key"
          name="roleKey"
          disabled={!!currentRole?.id}
          value={role.roleKey}
          onChange={handleChange}
        />
        <Input
          id="role-name"
          label="Role Name"
          name="roleName"
          value={role.roleName}
          onChange={handleChange}
        />
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900">
            Permissions
          </legend>
          <div className="space-y-2 mt-2">
            {permissionsList.map((permission) => (
              <CheckBox
                key={permission.id}
                id={permission.id}
                label={permission.name}
                helperText={permission.description}
                checked={role.permissions.includes(permission.id)}
                onChange={(e) => handlePermissionChange(e, permission.id)}
              />
            ))}
          </div>
        </fieldset>
      </div>
      <div className="mt-6 flex items-center justify-end gap-x-4">
        {actions}
        <Button
          loading={mutation.isPending}
          onClick={() => mutation.mutate(role)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
